import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';

/**
 * The status pills above the orders table, in template order. `'all'` is the
 * unfiltered view and `'archived'` is the odd one out: it filters on the
 * archived flag rather than on a status, so the pill key — not `status` — is
 * what identifies the view. See `applyPill`.
 */
type OrderPillKey =
  | 'all'
  | 'draft'
  | 'to_be_validated'
  | 'requested'
  | 'sent'
  | 'pending_inventory'
  | 'cancel'
  | 'declined'
  | 'sale'
  | 'done'
  | 'archived';

const ORDER_PILL_KEYS: OrderPillKey[] = [
  'all',
  'draft',
  'to_be_validated',
  'requested',
  'sent',
  'pending_inventory',
  'cancel',
  'declined',
  'sale',
  'done',
  'archived',
];

type StatusQuickFilter = { key: Exclude<OrderPillKey, 'all'>; labelKey: string; separatorBefore?: boolean };

/**
 * The three statuses the ADV team actually reaches for, shown as buttons on the
 * bar itself so the common case is one click and the current counts are visible
 * without opening anything (WB-8073).
 */
const STATUS_PRIMARY: StatusQuickFilter[] = [
  { key: 'draft', labelKey: 'common.draft' },
  { key: 'to_be_validated', labelKey: 'common.to_be_validated' },
  { key: 'done', labelKey: 'common.sent_to_erp' },
];

/**
 * Everything else, behind the "+N more" overflow. `archived` is last and
 * separated: it is not a status but an archived-flag view, so it must not read
 * as one more entry in the list.
 */
const STATUS_OVERFLOW: StatusQuickFilter[] = [
  { key: 'requested', labelKey: 'quotations_and_orders.quotations_request' },
  { key: 'sent', labelKey: 'quotations_and_orders.QuotationStatus.sent' },
  { key: 'pending_inventory', labelKey: 'common.pending_inventory' },
  { key: 'sale', labelKey: 'quotations_and_orders.list_quotations.order_completed' },
  { key: 'cancel', labelKey: 'common.cancelled' },
  { key: 'declined', labelKey: 'common.rejected' },
  { key: 'archived', labelKey: 'quotations_and_orders.list_quotations.archived', separatorBefore: true },
];

/**
 * Backend filter keys the quick-filter bar owns (WB-8073).
 *
 * They live in `filterState.advanced` so the bar and the advanced dialog can
 * never disagree about who the list is filtered to — but the CONTROL that shows
 * them is the bar, so the advanced filter button neither lights for them nor
 * clears them. Each control clears exactly what it displays.
 */
const QUICK_FILTER_KEYS = new Set(['my_orders', 'owner_id']);

/** Key this table's navigation-scoped row anchor is stored under. */
const ORDERS_TABLE_KEY = 'orders';

type ListColumnKey =
  | 'customer'
  | 'location'
  | 'source'
  | 'volta_id'
  | 'erp_id'
  | 'buyer_po'
  | 'quote_id'
  | 'catalog'
  | 'order_creator'
  | 'order_owner'
  | 'order_lines'
  | 'order_amount'
  | 'margin'
  | 'status'
  | 'erp_sync_status'
  | 'created'
  | 'last_update'
  | 'actions';

/**
 * Maps a list-view column key to the backend ``wl.field_visibility_entry`` key
 * for the order module. Only columns that correspond to a hideable field are
 * listed; everything else is governed solely by the per-user column preference.
 */
const COLUMN_TO_FIELD_KEY: Partial<Record<ListColumnKey, string>> = {
  buyer_po: 'customer_po_number',
  quote_id: 'quote_id',
  catalog: 'pricelist_name',
  order_creator: 'order_creator',
  order_owner: 'owner',
};

interface IListColumnConfig {
  label: string;
  visible: boolean;
  locked?: boolean;
}
import { DATE_TIME_FORMAT, DATE_FORMAT, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import {
  IQuotation,
  IQuotationFilter,
  IQuotationFilterRequest,
  IQuotationKPI,
  IQuotationScore,
  IStatusCount,
  IProcessingCount,
} from '../../models/quotations.models';
import { IOrderUser } from 'src/app/features/orders/models/order.model';
import { resolveAttentionCount, resolveLineCount } from '../../utils/line-composition.util';
import {
  combineLatest,
  debounceTime,
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { selectCanDo, selectHasOwnDataScope } from 'src/app/core/store/permissions/permissions.selectors';
import { selectHiddenFieldsForModule } from 'src/app/core/store/field-visibility/field-visibility.selectors';
import { ITableColumnSetting, TablePreferencesService } from 'src/app/shared/table-preferences.service';
import { IListViewDefaults, IListViewState, ListSortMode, ListViewStateService } from 'src/app/shared/list-view-state.service';
import {
  ArchiveQuotation,
  BulkArchiveQuotations,
  BulkArchiveQuotationsSuccess,
  BulkDeleteQuotations,
  BulkDeleteQuotationsSuccess,
  BulkDuplicateQuotations,
  BulkDuplicateQuotationsSuccess,
  BulkUnarchiveQuotations,
  BulkUnarchiveQuotationsSuccess,
  deleteQuotation,
  DismissDuplicateBadge,
  DuplicateQuotation,
  exportQuotation,
  GetQuotations,
  GetQuotationsSuccess,
  MarkQuotationRead,
  RefreshQuotations,
  SetIsCopilot,
  SetQuotationFilter,
  SetQuotationOwner,
  ToggleProcessingVisibility,
  UnarchiveQuotation,
} from '../../store/quotations.actions';
import { AtoProcessingStatus } from '../../enum/quotations.status.enum';
import { IPagination, IQuotePageCount } from 'src/app/shared/models/page.model';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { encodeFilterObject, formatResponseTime, parseFilterRequest, refillFilter } from 'src/app/shared/utils/helpers';
import { userInitials } from 'src/app/shared/utils/order-users-dedupe';
import { getUserColor } from 'src/app/shared/utils/user-color.util';
import { getQuotationStatusAccent, getQuotationStatusStyles, getErpSyncStatusStyles as getErpSyncStatusStylesUtil } from 'src/app/shared/utils/quotation-status-styles';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { FilterQuotationsComponent } from '../filter-quotations/filter-quotations.component';
import { MatDialog } from '@angular/material/dialog';
import { NavigationService } from 'src/app/shared/router.service';
import { DetailsCopilotScoreComponent } from '../details-copilot-score/details-copilot-score.component';
import { CompaniesSelectionDialogComponent } from '../../../new-orders/components/companies-selection-dialog/companies-selection-dialog.component';
import { KillStockDetailsComponent } from '../kill-stock-details/kill-stock-details.component';
import { OrderStatusConfirmComponent } from '../order-status-confirm/order-status-confirm.component';
import { DialogAction, DialogProps } from '../order-status-confirm/order-status-confirm.types';
import { InboundChannelService } from 'src/app/features/orders/services/inbound-channel.service';
import { IInboundChannel } from 'src/app/features/orders/models/order.model';
import { OrdersService } from 'src/app/features/orders/services/orders.services';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { IFilterSelectOption } from 'src/app/ui/filter-select/filter-select.component';
import { NO_SELECTION_MODIFIERS, SelectionModifiers } from 'src/app/shared/models/selection.models';
import { RangeSelectionTracker } from 'src/app/shared/utils/range-selection';
import { FTUE_ANCHORS } from 'src/app/core/ftue/ftue-anchors';
import { FtuePresenterService } from 'src/app/core/ftue/ftue-presenter.service';
import { FTUE_TIP_IDS } from 'src/app/core/ftue/ftue-tip.registry';

@Component({
  selector: 'app-list-quotations',
  templateUrl: './list-quotations.component.html',
  styleUrls: ['./list-quotations.component.scss'],
})
export class ListQuotationsComponent implements OnInit, OnDestroy {
  page: IQuotePageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1,
    order: 'desc',
    co_pilot: 0,
  };
  expandedIndex: number = -1;
  isSuccess: boolean = false;
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.quotations.metadata);
  DATE_FORMAT = DATE_TIME_FORMAT;
  STANDARD_DATE_FORMAT = DATE_FORMAT;
  quotations$: Observable<IQuotation[]> = this.store.select((state) => state.quotations.quotations);
  showProcessing$: Observable<boolean> = this.store.select((state) => state.quotations.showProcessing);
  processing$: Observable<IQuotation[]> = this.store.select((state) => state.quotations.processing);
  processingCount$: Observable<IProcessingCount> = this.store.select((state) => state.quotations.processing_count);
  displayQuotations$: Observable<IQuotation[]> = combineLatest([this.quotations$, this.processing$, this.showProcessing$]).pipe(
    map(([quotations, processing, showProcessing]) => {
      const normalRows = quotations.filter((quote) => !this.isPlaceholder(quote));
      return showProcessing ? [...processing, ...normalRows] : normalRows;
    }),
  );
  now = Date.now();
  private destroy$ = new Subject<void>();
  kpi$: Observable<IQuotationKPI | undefined> = this.store.select((state) => state.quotations.kpi);
  status_count$: Observable<IStatusCount | undefined> = this.store.select((state) => state.quotations.status_count);
  quotations: IQuotation[] = [];
  displayQuotations: IQuotation[] = [];
  subscriptions: Subscription[] = [];
  isCopilot$: Observable<boolean> = this.store.select((state) => state.quotations.filterState.isCopilot);
  selectedStatus: OrderPillKey = 'all';

  /** Scroll container the row anchor is restored inside (WB-7867). */
  @ViewChild('tableWrapper') tableWrapper?: ElementRef<HTMLElement>;

  /**
   * Whitelists and fallbacks for hydrating this view out of the URL. `sortKeys`
   * is intentionally left open: sortable columns are driven by the column
   * config, so pinning them here would mean two lists to keep in step.
   */
  private readonly viewDefaults: IListViewDefaults = {
    size: DEFAULT_PAGE_SIZE,
    pill: 'all',
    sortBy: 'id',
    sortMode: 'desc',
    pageSizes: PAGE_SIZE_OPTIONS,
    pills: ORDER_PILL_KEYS,
  };

  /** Role-allowed columns passed to the <ui-table-column-settings> popover. */
  pickerColumns: ITableColumnSetting[] = [];
  /** Current column order (configurable columns only; excludes select/copilot/actions). */
  columnOrder: ListColumnKey[] = [];
  inboundChannel: IInboundChannel | null = null;
  channelCopied = false;
  private channelCopyTimer: ReturnType<typeof setTimeout> | null = null;
  private inboundChannelLoaded = false;

  public getStatusTranslation(status: string): string {
    return this.translateService.instant(`quotations_and_orders.QuotationStatus.${status}`) || status;
  }

  public getDescriptionStatusTranslation(status: string): string {
    return this.translateService.instant(`quotations_and_orders.QuotationDescriptionStatus.${status}`) || status;
  }

  filter: IQuotationFilterRequest | undefined = undefined;
  key: string = '';
  status: string = '';
  searchInput = new FormControl('');
  isCopilot: boolean = false;
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  formatResponseTime = formatResponseTime;
  userInitials = userInitials;
  getUserColor = getUserColor;

  activeSortBy: string = 'id';
  activeSortMode: ListSortMode = 'desc';

  unparsedFilter: IQuotationFilter | null = null;
  module: string = 'quotation';

  archived: number = 0;

  selectedQuotations: IQuotation[] = [];

  /** Pivot for Shift+click range selection across the orders table. */
  private readonly rangeSelection = new RangeSelectionTracker();

  // Rows the user can tick: normal orders + failed (needs_review) placeholders.
  // Actively-processing rows have no checkbox, so they are never selectable.
  get selectableQuotations(): IQuotation[] {
    return (this.displayQuotations ?? []).filter((q) => q.ato_processing_status !== 'processing');
  }

  get isAllSelected(): boolean {
    const list = this.selectableQuotations;
    return list.length > 0 && list.every((q) => this.selectedQuotations.some((s) => s.id === q.id));
  }

  get isPartialSelected(): boolean {
    return this.selectedQuotations.length > 0 && !this.isAllSelected;
  }

  get hasUndeletableInSelection(): boolean {
    return this.selectedQuotations.some((q) => !this.canDelete(q));
  }

  get deletableSelectionCount(): number {
    return this.selectedQuotations.filter((q) => this.canDelete(q)).length;
  }

  isQuoteSelected(quote: IQuotation): boolean {
    return this.selectedQuotations.some((s) => s.id === quote.id);
  }

  /**
   * Apply `checked` to the clicked row, or — with Shift held — to every
   * selectable row between the range anchor and the clicked row. Ctrl/Cmd
   * toggles the single row while keeping the anchor, so a hole can be punched
   * in a selection without losing the pivot.
   */
  toggleOne(quote: IQuotation, checked: boolean, modifiers: SelectionModifiers = NO_SELECTION_MODIFIERS): void {
    const rows = this.selectableQuotations;
    const affected = this.rangeSelection.resolve(
      rows,
      rows.findIndex((q) => q.id === quote.id),
      modifiers,
    );
    if (!affected.length) return;

    const affectedIds = new Set(affected.map((q) => q.id));
    const remaining = this.selectedQuotations.filter((q) => !affectedIds.has(q.id));

    this.selectedQuotations = checked ? [...remaining, ...affected] : remaining;
  }

  toggleAll(checked: boolean): void {
    this.selectedQuotations = checked ? [...this.selectableQuotations] : [];
    // Select-all is not a row click, so it must not leave a stale pivot behind.
    this.rangeSelection.reset();
  }

  /** Empty the selection — used by the floating bulk-actions bar's clear button. */
  clearSelection(): void {
    this.toggleAll(false);
  }

  private static readonly THIS_TABLE_KEY = 'orders_list_table';

  columns: Record<ListColumnKey, IListColumnConfig> = {
    customer: { label: 'quotations_and_orders.customer', visible: true, locked: true },
    location: { label: 'quotations_and_orders.location', visible: true },
    source: { label: 'quotations_and_orders.source', visible: true, locked: true },
    volta_id: { label: 'quotations_and_orders.volta_id', visible: true, locked: true },
    erp_id: { label: 'quotations_and_orders.list_quotations.erp_id', visible: true },
    buyer_po: { label: 'companies.customer_po_number', visible: true },
    quote_id: { label: 'quotations_and_orders.quote_id', visible: true },
    catalog: { label: 'Catalogs.catalog_name', visible: true },
    order_creator: { label: 'quotations_and_orders.order_creator_col', visible: true },
    order_owner: { label: 'quotations_and_orders.order_owner_col', visible: true },
    order_lines: { label: 'quotations_and_orders.items', visible: true },
    order_amount: { label: 'quotations_and_orders.order_amount', visible: true },
    margin: { label: 'quotations_and_orders.margin', visible: true },
    status: { label: 'common.status', visible: true, locked: true },
    // WB-7957: only ever offered/shown when featureFlags.asyncErpSyncEnabled is
    // true -- see isColumnAllowedByFeatureFlag / DEFAULT_ORDER below.
    erp_sync_status: { label: 'quotations_and_orders.erp_sync_status_col', visible: true },
    created: { label: 'quotations_and_orders.list_quotations.created', visible: true },
    last_update: { label: 'quotations_and_orders.last_update', visible: true },
    actions: { label: 'quotations_and_orders.actions.title', visible: true, locked: true },
  };

  /** Value for the "Order lines" cell — see resolveLineCount. */
  orderLinesCount(quote: IQuotation): number {
    return resolveLineCount(quote.line_composition, quote);
  }

  /** Amber badge count: needs review + unmatched. 0 hides the badge. */
  attentionCount(quote: IQuotation): number {
    return resolveAttentionCount(quote.line_composition);
  }

  private static readonly ERP_STATUSES = ['done', 'sent_to_erp'];
  private static readonly DELETE_BLOCKED_STATUSES = ['done', 'sent_to_erp'];

  isErpSynced(quote: IQuotation): boolean {
    return ListQuotationsComponent.ERP_STATUSES.includes(quote.status) || !!quote.external_id;
  }

  onOwnerChanged(quote: IQuotation, owner: IOrderUser): void {
    // Update the row immutably through the store. We deliberately do NOT refetch
    // the list: a GET issued immediately after the PATCH can hit a read replica
    // that hasn't caught up and would clobber this with the stale previous owner.
    // (Store objects are frozen, so a direct mutation would throw.)
    this.store.dispatch(SetQuotationOwner({ id: quote.id, owner }));
  }

  canArchive(quote: IQuotation): boolean {
    return !quote.is_archived;
  }

  canUnarchive(quote: IQuotation): boolean {
    return !!quote.is_archived;
  }

  canDelete(quote: IQuotation): boolean {
    return !ListQuotationsComponent.DELETE_BLOCKED_STATUSES.includes(quote.status);
  }

  canEditOrders$: Observable<boolean> = this.store.select(selectCanDo('order', 'edit'));
  canDeleteOrders$: Observable<boolean> = this.store.select(selectCanDo('order', 'delete'));
  private hiddenOrderFields = new Set<string>();
  private hiddenOrderFields$ = this.store.select(selectHiddenFieldsForModule('order'));
  canShowRowActions$: Observable<boolean> = combineLatest([this.canEditOrders$, this.canDeleteOrders$]).pipe(
    map(([canEdit, canDelete]) => canEdit || canDelete),
  );

  /** Anchor keys the template exposes to the FTUE framework (WB-7535). */
  readonly ftueAnchors = FTUE_ANCHORS;

  // ── Quick filters (WB-8073) ─────────────────────────────────────────────────
  // Status rides the existing pill state (`selectedStatus` / `applyPill`), so it
  // keeps round-tripping through the URL exactly as the pill row did. Owner and
  // "assigned to me" write into `ownershipFilter`, the SAME slice the advanced
  // filter dialog owns — one source of truth, so the two controls can never
  // disagree about who the list is filtered to.

  /** Users offered in the owner dropdown. Loaded on first open, then searched. */
  ownerOptions: IOrderUser[] = [];
  ownerOptionsLoading = false;
  private ownerOptionsLoaded = false;
  /**
   * Every owner lookup goes through here so they can be `switchMap`-ed. Without
   * that, the unfiltered request the menu fires on open races the debounced
   * search that follows it ~250ms later, and because the unfiltered payload is
   * the bigger one it can land second and replace the search results with the
   * full list.
   */
  private readonly ownerLookup$ = new Subject<{ term: string; initial: boolean }>();
  /** Whether the role can only ever see its own data — the picker is a no-op then. */
  isOwnershipScopeForced$: Observable<boolean> = this.store.select(selectHasOwnDataScope('order'));
  /**
   * The signed-in user, so their row in the owner list can be marked "ME". This
   * is what replaced the separate "assigned to me" button (WB-8073): one list of
   * people, with yours labelled, rather than a list plus a button that filtered
   * on the same thing.
   */
  private readonly currentUserId: number | null = this.authService.getCurrentUser()?.id ?? null;

  // Both menus are built from the template, so their contents are memoised on
  // the inputs they derive from (counts / user list / active language) to keep
  // change detection from handing `ui-filter-select` a fresh array every cycle.
  private statusOptionsCache: {
    counts: IStatusCount | null;
    lang: string;
    options: { primary: IFilterSelectOption[]; overflow: IFilterSelectOption[] };
  } | null = null;
  private ownerOptionsCache: { users: IOrderUser[]; selectedId: number | null; lang: string; options: IFilterSelectOption[] } | null =
    null;

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private navigationService: NavigationService,
    private tablePrefs: TablePreferencesService,
    private listViewState: ListViewStateService,
    private inboundChannelService: InboundChannelService,
    private ftuePresenter: FtuePresenterService,
    private ordersService: OrdersService,
    private authService: AuthService,
    public featureFlags: FeatureFlagService
  ) {
    this.hiddenOrderFields$.subscribe((keys) => {
      this.hiddenOrderFields = new Set(keys ?? []);
      // Role visibility can change after init — rebuild the picker contents.
      this.pickerColumns = this.buildPickerColumns(this.columnOrder);
    });
    this.activatedRoute.queryParams.subscribe((params) => {
      const view = this.listViewState.fromQueryParams(params, this.viewDefaults);

      if (params['keyword'] !== undefined) {
        this.key = view.keyword;
        this.searchInput.setValue(this.key);
      }
      if (view.filter) {
        try {
          this.unparsedFilter = refillFilter(this.module, JSON.parse(decodeURIComponent(view.filter))) as IQuotationFilter;
          if (this.unparsedFilter) this.filter = parseFilterRequest(this.module, this.unparsedFilter);
        } catch (error) {
          this.filter = undefined;
        }
      }
      // WB-8072: the pill and sort used to live only on this component, so both
      // reset to their defaults every time the list was recreated — which is
      // what "my filter is lost" meant. They now round-trip through the URL,
      // which covers every return path that carries query params: the detail
      // page's back action and its post-validation redirect both navigate with
      // `queryParamsHandling: 'merge'`.
      //
      // It does not cover the breadcrumb or the sidebar, which link to a bare
      // `/quotations` and so still land on the default view. Restoring those
      // would mean either teaching those links to preserve params or mirroring
      // the pill into `filterState` the way the advanced filter is; both are
      // wider than this fix and neither is the flow the ticket reports.
      this.applyPill(view.pill as OrderPillKey);
      this.activeSortBy = view.sortBy;
      this.activeSortMode = view.sortMode;
      this.page = { ...this.page, number: view.page, size: view.size };
    });
  }

  /**
   * Point the request params at a status pill. `'all'` clears both filters and
   * `'archived'` filters on the archived flag with no status; every other key
   * is a status. Keeping this in one place is what lets the pill survive a
   * round-trip as a single URL value.
   */
  private applyPill(pill: OrderPillKey): void {
    this.selectedStatus = pill;
    this.status = pill === 'all' || pill === 'archived' ? '' : pill;
    this.archived = pill === 'archived' ? 1 : 0;
  }

  /** The current view as the URL should describe it. */
  private currentView(): IListViewState {
    return {
      page: this.page.number,
      size: this.page.size,
      keyword: this.key ?? '',
      filter: encodeFilterObject(this.unparsedFilter),
      pill: this.selectedStatus,
      sortBy: this.activeSortBy,
      sortMode: this.activeSortMode,
    };
  }

  /**
   * Serialised view for `router.navigate`, with `isCopilot` merged in. Copilot
   * is not part of the shared view contract — it is an orders-only mode that
   * also lives in the NgRx filter state — so it is appended here rather than
   * pushed into `ListViewStateService`.
   */
  private listQueryParams(overrides: Partial<IListViewState> = {}): { [key: string]: any } {
    const params = this.listViewState.toQueryParams({ ...this.currentView(), ...overrides }, this.viewDefaults);
    return { ...params, isCopilot: this.isCopilot ? 1 : null };
  }

  isColumnVisible(key: ListColumnKey): boolean {
    if (!this.columns?.[key]?.visible) return false;
    if (!this.isColumnAllowedByFeatureFlag(key)) return false;
    return this.isColumnAllowedByRole(key);
  }

  /**
   * True unless `key` is behind a feature flag that's currently off (WB-7957:
   * erp_sync_status only exists at all while asyncErpSyncEnabled is true).
   * Mirrors isColumnAllowedByRole, but for flags instead of role visibility.
   */
  isColumnAllowedByFeatureFlag(key: ListColumnKey): boolean {
    if (key === 'erp_sync_status') return this.featureFlags.asyncErpSyncEnabled;
    return true;
  }

  /**
   * True when the role does not hide the underlying catalog field, regardless
   * of the user's column preference. Used to gate the per-user dropdown so
   * users never see a toggle for a column they cannot reveal.
   */
  isColumnAllowedByRole(key: ListColumnKey): boolean {
    const fieldKey = COLUMN_TO_FIELD_KEY[key];
    if (!fieldKey) return true;
    return !this.hiddenOrderFields.has(fieldKey);
  }

  /**
   * True when `key` is a pricing/metrics column AND this specific order was
   * created while the company's "hide pricing columns" setting was on
   * (WB-7024). Unlike the details/side-by-side views, the list can't hide a
   * whole column for one row — other rows may still need it — so instead the
   * template masks just this row's value for that column.
   */
  isPricingMaskedForRow(quotation: IQuotation, key: ListColumnKey): boolean {
    return !!quotation.hide_pricing_columns && ListQuotationsComponent.PRICING_KEYS.has(key);
  }

  // Configurable column order. Excludes the leading select cell, the copilot
  // score column, and the trailing actions cell — those are fixed.
  private static readonly DEFAULT_ORDER: ListColumnKey[] = [
    'customer', 'location', 'source', 'volta_id', 'erp_id', 'buyer_po', 'quote_id', 'catalog',
    'order_creator', 'order_owner', 'order_lines', 'order_amount', 'margin',
    'status', 'erp_sync_status', 'created', 'last_update',
  ];

  /** Anchored to the front: immovable, nothing precedes them. */
  private static readonly PINNED_KEYS = new Set<ListColumnKey>(['customer']);
  /** Cannot be hidden (but may still be reordered unless also pinned). */
  private static readonly NON_HIDEABLE_KEYS = new Set<ListColumnKey>([
    'customer', 'source', 'volta_id', 'status',
  ]);
  /**
   * Pricing / metrics columns whose VALUE (not the column itself — other rows
   * still need it) is masked for a given row when that row's own order was
   * created with the company's "hide pricing columns" setting on (WB-7024).
   * See isPricingMaskedForRow().
   */
  private static readonly PRICING_KEYS = new Set<ListColumnKey>(['order_amount', 'margin']);

  /** Visible configurable columns in display order — drives the table rendering. */
  get orderedColumns(): ListColumnKey[] {
    return this.columnOrder.filter((key) => this.isColumnVisible(key));
  }

  /** Hydrate column visibility + order from stored user preferences. */
  private initColumns(): void {
    // The SKUs column became "Order lines" (WB-8066): same slot, but counting
    // every line instead of distinct matched products. Renaming the stored key
    // keeps each user's chosen position and visibility -- without it the new
    // column would be appended at the far right of the table for everyone who
    // has ever reordered this list.
    this.tablePrefs.renameKeysOnce(
      ListQuotationsComponent.THIS_TABLE_KEY,
      { skus: 'order_lines' },
      'wb-8066-skus-to-order-lines',
    );

    const stored = this.tablePrefs.applyPreferences(
      ListQuotationsComponent.THIS_TABLE_KEY,
      this.buildDefaultColumnSettings()
    );
    stored.forEach((column) => this.applyVisibility(column));
    this.columnOrder = this.pinnedFirst(stored.map((column) => column.key as ListColumnKey));
    this.pickerColumns = this.buildPickerColumns(this.columnOrder);
  }

  /** Keep pinned columns at the front regardless of any stale saved order. */
  private pinnedFirst(keys: ListColumnKey[]): ListColumnKey[] {
    const pinned = keys.filter((key) => ListQuotationsComponent.PINNED_KEYS.has(key));
    const rest = keys.filter((key) => !ListQuotationsComponent.PINNED_KEYS.has(key));
    return [...pinned, ...rest];
  }

  /** Emitted by <ui-table-column-settings> on reorder / show / hide. */
  onColumnsChange(columns: ITableColumnSetting[]): void {
    // Only persist columns whose visibility actually changed here — columns
    // the user didn't touch (e.g. a pure reorder, or an untouched column
    // carried along in this emission) must keep falling through to the
    // computed default so a company-level setting change still applies later.
    const changedVisibility: Record<string, boolean> = {};
    columns.forEach((column) => {
      const key = column.key as ListColumnKey;
      const wasVisible = this.columns[key]?.visible;
      const nowVisible = this.columns[key]?.locked ? true : column.visible;
      if (wasVisible !== nowVisible) {
        changedVisibility[key] = nowVisible;
      }
      this.applyVisibility(column);
    });
    this.columnOrder = this.pinnedFirst(columns.map((column) => column.key as ListColumnKey));
    this.pickerColumns = columns;
    this.tablePrefs.save(ListQuotationsComponent.THIS_TABLE_KEY, columns, changedVisibility);
  }

  private applyVisibility(column: ITableColumnSetting): void {
    const target = this.columns[column.key as ListColumnKey];
    if (target) target.visible = target.locked ? true : column.visible;
  }

  private buildDefaultColumnSettings(): ITableColumnSetting[] {
    return ListQuotationsComponent.DEFAULT_ORDER.map((key) => this.toColumnSetting(key));
  }

  /** Picker contents: current order, role-allowed and feature-flag-allowed columns only. */
  private buildPickerColumns(order: ListColumnKey[]): ITableColumnSetting[] {
    return order
      .filter((key) => this.isColumnAllowedByRole(key) && this.isColumnAllowedByFeatureFlag(key))
      .map((key) => this.toColumnSetting(key));
  }

  private toColumnSetting(key: ListColumnKey): ITableColumnSetting {
    return {
      key,
      label: this.columns[key].label,
      visible: this.columns[key].visible,
      pinned: ListQuotationsComponent.PINNED_KEYS.has(key),
      hideable: !ListQuotationsComponent.NON_HIDEABLE_KEYS.has(key),
    };
  }

  ngOnInit(): void {
    this.initColumns();
    this.watchOwnerLookups();

    // Feature announcement: this table is personalisable. The presenter owns
    // whether that actually shows anything — seen state, budget and cooldown —
    // so requesting it on every visit is correct, not wasteful.
    this.ftuePresenter.request(FTUE_TIP_IDS.ORDERS_COLUMN_PERSONALIZATION);
    // Restore filter from URL on page refresh / direct load
    if (this.unparsedFilter || this.key) {
      this.store.dispatch(
        SetQuotationFilter({
          keyword: this.key,
          advanced: this.unparsedFilter ?? null,
        }),
      );
    }

    const sub4 = this.store
      .select((state) => state.quotations.filterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe((filter) => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.filters.advanced = filter.advanced;
        this.filters.keyword = filter.keyword;
        this.key = filter.keyword;
        this.searchInput.setValue(this.key);
        if (this.isCopilot != filter.isCopilot) {
          this.page = { ...this.page, number: 1 };
        }
        this.isCopilot = filter.isCopilot;
        this.toPage(this.page);
      });

    const sub1 = this.quotations$.subscribe((quotations: IQuotation[]) => {
      if (quotations) {
        this.quotations = quotations;
      }
    });

    const sub3 = this.actions$
      .pipe(
        ofType(GetQuotationsSuccess),
        tap((action) => {
          this.isSuccess = true;
          this.expandedIndex = -1;
          this.restorePendingAnchor();
        }),
      )
      .subscribe();

    this.subscriptions.push(sub1, sub3, sub4);

    this.actions$.pipe(ofType(RefreshQuotations), takeUntil(this.destroy$)).subscribe(() => this.toPage(this.page));

    // Mirror the rendered list (normal rows + processing/needs_review placeholders)
    // so select-all can reach the placeholder rows, not just this.quotations.
    this.displayQuotations$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      this.displayQuotations = list ?? [];
      // Rows moved (page, sort, filter, refresh) so the stored range anchor no
      // longer points at the row the user last clicked.
      this.rangeSelection.reset();
    });

    timer(0, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.now = Date.now()));
  }

  /**
   * Scroll back to the row the user opened, if one is pending (WB-7867).
   *
   * Deferred by a macrotask because this runs off `GetQuotationsSuccess`, i.e.
   * before Angular has rendered the rows the action carries — querying for the
   * anchor row synchronously would find nothing. The anchor is consumed either
   * way, so a row that has dropped out of the current view (validating an order
   * changes its status) degrades to staying at the top rather than retrying.
   */
  private restorePendingAnchor(): void {
    const rowId = this.listViewState.consumeAnchor(ORDERS_TABLE_KEY);
    if (rowId === null) return;
    setTimeout(() => this.listViewState.restoreAnchor(this.tableWrapper?.nativeElement, rowId), 0);
  }

  applyCopilot() {
    this.isCopilot$.pipe(take(1)).subscribe((isCopilot) => {
      this.store.dispatch(SetIsCopilot({ isCopilot: !isCopilot }));
    });
  }

  setFilter() {
    const dialogRef = this.dialog.open(FilterQuotationsComponent, {
      width: '900px',
      maxHeight: '90vh',
      panelClass: 'filter-quotations-dialog',
    });
    dialogRef.afterClosed().subscribe((filterResult: any) => {
      if (filterResult) {
        this.selectedQuotations = [];
        this.page = { ...this.page, number: 1 };
        this.filters.advanced = filterResult;
        this.store.dispatch(
          SetQuotationFilter({
            keyword: this.key,
            advanced: filterResult,
          }),
        );
      }
    });
  }

  toPage(page: IQuotePageCount) {
    this.selectedQuotations = [];
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(
      GetQuotations({
        page: { ...this.page, co_pilot: this.isCopilot ? 1 : 0 },
        filter: this.filter,
        key: this.key,
        sort_by: this.activeSortBy,
        sort_mode: this.activeSortMode,
        status: this.status,
        archived: this.archived,
      }),
    );
    this.router.navigate([], {
      queryParams: this.listQueryParams(),
      queryParamsHandling: 'merge',
    });
  }

  toggleVariants(id: number) {
    this.expandedIndex = this.expandedIndex === id ? -1 : id;
  }

  addQuotation(targetId?: number) {
    const dialogRef = this.dialog.open(CompaniesSelectionDialogComponent, {
      data: { company: null, canGoBack: false, targetId },
    });

    dialogRef.afterClosed().subscribe((selectedLocation) => {
      if (selectedLocation) {
        const id = selectedLocation.id;
        this.router.navigate(['/quotations', id], {
          queryParams: { location_id: id },
        });
      }
    });
  }

  async exportQuotations(): Promise<void> {
    const copilot = await firstValueFrom(this.isCopilot$);
    this.store.dispatch(exportQuotation({ filter: this.filter, key: this.key, co_pilot: copilot ? 1 : 0 }));
  }

  getQuotationsStatus(pill: OrderPillKey): boolean | void {
    if (this.selectedStatus === pill) return true;
    this.selectedQuotations = [];
    this.applyPill(pill);
    this.page = { ...this.page, number: 1 };
    this.toPage(this.page);
  }

  getQuoteByStatus(quotations: IQuotation[], status: string): number {
    return quotations.filter((quote) => quote.status == status).length;
  }

  getStatusStyles(status: string): { [key: string]: string } {
    return getQuotationStatusStyles(status);
  }

  getErpSyncStatusStyles(status: string): { [key: string]: string } {
    return getErpSyncStatusStylesUtil(status);
  }

  getErpSyncStatusTranslation(status: string): string {
    return this.translateService.instant(`quotations_and_orders.ErpSyncStatus.${status}`) || status;
  }

  clearFilters() {}

  applyFilter() {}

  setToComp(status: string) {
    // Set the route to the component
    if (status === 'Quotation requested + 1') 'this.searchValue';
    if (status === 'Accepted') 'this.searchValue';
  }

  sortBy(key: string, mode: ListSortMode) {
    if (key == this.activeSortBy && mode == this.activeSortMode) return;

    this.selectedQuotations = [];
    this.activeSortBy = key;
    this.activeSortMode = mode;
    // Routed through toPage so the new sort reaches the URL and the page reset
    // is reflected there too; this used to dispatch straight to the store, so
    // sorting left `page` stale in the URL and the sort itself unrecorded.
    this.page = { ...this.page, number: 1 };
    this.toPage(this.page);
  }

  details(id: number) {
    // Opening an order clears its unread indicator (order-level, shared).
    this.store.dispatch(MarkQuotationRead({ id }));
    // WB-7867: remember the row so returning lands on it instead of the top.
    this.listViewState.rememberAnchor(ORDERS_TABLE_KEY, id);
    this.router.navigate(['/quotations/', id], {
      queryParams: this.listQueryParams(),
      queryParamsHandling: 'merge',
    });
  }

  isPlaceholder(quote: IQuotation): boolean {
    return quote.ato_processing_status === AtoProcessingStatus.processing || quote.ato_processing_status === AtoProcessingStatus.needs_review;
  }

  elapsedFor(startIso?: string | null): string {
    if (!startIso) return '0s';
    // Odoo serializes Datetime as naive UTC ("YYYY-MM-DD HH:MM:SS"). Without a
    // timezone marker `new Date()` parses it as local time, inflating the elapsed
    // value by the viewer's UTC offset. Normalise to an explicit UTC instant.
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(startIso);
    const normalised = startIso.includes('T') ? startIso : startIso.replace(' ', 'T');
    const start = new Date(hasZone ? normalised : `${normalised}Z`).getTime();
    if (isNaN(start)) return '0s';
    const totalSeconds = Math.max(0, Math.floor((this.now - start) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  createManually(quote: IQuotation): void {
    this.addQuotation(quote.id);
  }

  deleteProcessingRow(quote: IQuotation): void {
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.status_dialog.delete_order.title'),
      description: this.translateService.instant('quotations_and_orders.status_dialog.delete_order.description'),
      mainIcon: 'assets/icones/main_delete_icon.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.status_dialog.actions.delete_order'),
      confirmClass: 'action-cancel',
      confirmIcon: 'assets/icones/cancel_icon_white.svg',
      onConfirm: () => this.store.dispatch(deleteQuotation({ id: quote.id, confirm: true })),
    });
  }

  viewOrdersScore(quote: IQuotation) {
    if (!quote.score) return;
    this.dialog.open(DetailsCopilotScoreComponent, {
      data: {
        quote_name: quote.name,
        score: quote.score,
        overall_score: quote.final_score,
      },
    });
  }

  duplicateQuotation(quote: IQuotation, event?: MouseEvent) {
    event?.stopPropagation();
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.confirm.duplicate_title'),
      description: this.translateService.instant('quotations_and_orders.confirm.duplicate_body', { name: quote.name }),
      mainIcon: 'assets/icones/copy.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.actions.duplicate'),
      confirmClass: 'action-primary',
      onConfirm: () => this.store.dispatch(DuplicateQuotation({ id: quote.id })),
    });
  }

  dismissDuplicateBadge(quote: IQuotation): void {
    this.store.dispatch(DismissDuplicateBadge({ id: quote.id }));
  }

  archiveQuotation(quote: IQuotation, event?: MouseEvent) {
    event?.stopPropagation();
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.confirm.archive_title'),
      description: this.translateService.instant('quotations_and_orders.confirm.archive_body', { name: quote.name }),
      mainIcon: 'assets/icones/archive.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.actions.archive'),
      confirmClass: 'action-primary',
      onConfirm: () => this.store.dispatch(ArchiveQuotation({ id: quote.id, quotationName: quote.name })),
    });
  }

  unarchiveQuotation(quote: IQuotation, event?: MouseEvent) {
    event?.stopPropagation();
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.confirm.unarchive_title'),
      description: this.translateService.instant('quotations_and_orders.confirm.unarchive_body', { name: quote.name }),
      mainIcon: 'assets/icones/unarchive.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.actions.unarchive'),
      confirmClass: 'action-primary',
      onConfirm: () => this.store.dispatch(UnarchiveQuotation({ id: quote.id })),
    });
  }

  deleteQuotationFromList(quote: IQuotation, event?: MouseEvent) {
    event?.stopPropagation();
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.status_dialog.delete_order.title'),
      description: this.translateService.instant('quotations_and_orders.status_dialog.delete_order.description'),
      mainIcon: 'assets/icones/main_delete_icon.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.status_dialog.actions.delete_order'),
      confirmClass: 'action-cancel',
      confirmIcon: 'assets/icones/cancel_icon_white.svg',
      onConfirm: () => this.store.dispatch(deleteQuotation({ id: quote.id, confirm: true })),
    });
  }

  bulkDuplicate(): void {
    if (this.selectedQuotations.length === 0) return;
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.confirm.duplicate_title'),
      description: this.translateService.instant('quotations_and_orders.bulk.duplicate_body', { count: this.selectedQuotations.length }),
      mainIcon: 'assets/icones/copy.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.actions.duplicate'),
      confirmClass: 'action-primary',
      onConfirm: () => {
        const ids = this.selectedQuotations.map((q) => q.id);
        this.selectedQuotations = [];
        const sub = this.actions$.pipe(ofType(BulkDuplicateQuotationsSuccess), take(1)).subscribe(() => {
          sub.unsubscribe();
          this.toPage(this.page);
        });
        this.store.dispatch(BulkDuplicateQuotations({ ids }));
      },
    });
  }

  bulkArchive(): void {
    const toArchive = this.selectedQuotations.filter((q) => !q.is_archived);
    if (toArchive.length === 0) return;
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.confirm.archive_title'),
      description: this.translateService.instant('quotations_and_orders.bulk.archive_body', { count: toArchive.length }),
      mainIcon: 'assets/icones/archive.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.actions.archive'),
      confirmClass: 'action-primary',
      onConfirm: () => {
        const ids = toArchive.map((q) => q.id);
        this.selectedQuotations = [];
        const sub = this.actions$.pipe(ofType(BulkArchiveQuotationsSuccess), take(1)).subscribe(() => {
          sub.unsubscribe();
          this.toPage(this.page);
        });
        this.store.dispatch(BulkArchiveQuotations({ ids }));
      },
    });
  }

  bulkUnarchive(): void {
    if (this.selectedQuotations.length === 0) return;
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.confirm.unarchive_title'),
      description: this.translateService.instant('quotations_and_orders.bulk.unarchive_body', { count: this.selectedQuotations.length }),
      mainIcon: 'assets/icones/unarchive.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.actions.unarchive'),
      confirmClass: 'action-primary',
      onConfirm: () => {
        const ids = this.selectedQuotations.map((q) => q.id);
        this.selectedQuotations = [];
        const sub = this.actions$.pipe(ofType(BulkUnarchiveQuotationsSuccess), take(1)).subscribe(() => {
          sub.unsubscribe();
          this.toPage(this.page);
        });
        this.store.dispatch(BulkUnarchiveQuotations({ ids }));
      },
    });
  }

  bulkDelete(): void {
    const toDelete = this.selectedQuotations.filter((q) => this.canDelete(q));
    if (toDelete.length === 0) return;
    this.openConfirmDialog({
      title: this.translateService.instant('quotations_and_orders.status_dialog.delete_order.title'),
      description: this.translateService.instant('quotations_and_orders.bulk.delete_body', { count: toDelete.length }),
      mainIcon: 'assets/icones/main_delete_icon.svg',
      confirmLabel: this.translateService.instant('quotations_and_orders.status_dialog.actions.delete_order'),
      confirmClass: 'action-cancel',
      confirmIcon: 'assets/icones/cancel_icon_white.svg',
      onConfirm: () => {
        const ids = toDelete.map((q) => q.id);
        this.selectedQuotations = [];
        const sub = this.actions$.pipe(ofType(BulkDeleteQuotationsSuccess), take(1)).subscribe(() => {
          sub.unsubscribe();
          this.toPage(this.page);
        });
        this.store.dispatch(BulkDeleteQuotations({ ids }));
      },
    });
  }

  private openConfirmDialog(opts: {
    title: string;
    description: string;
    mainIcon?: string;
    confirmLabel: string;
    confirmClass: string;
    confirmIcon?: string;
    onConfirm: () => void;
  }) {
    const actions: DialogAction[] = [
      {
        label: this.translateService.instant('quotations_and_orders.status_dialog.actions.go_back'),
        class: 'action-light',
        callback: () => this.dialog.closeAll(),
      },
      {
        label: opts.confirmLabel,
        class: opts.confirmClass,
        ...(opts.confirmIcon
          ? { icon: { position: 'before' as const, name: opts.confirmIcon } }
          : {
              icon: {
                position: 'after',
                name: 'arrow_forward',
              },
            }),
        callback: () => {
          opts.onConfirm();
          this.dialog.closeAll();
        },
      },
    ];

    const dialogData: DialogProps = {
      title: opts.title,
      description: opts.description,
      mainIcon: opts.mainIcon,
      actions,
    };
    this.dialog.open(OrderStatusConfirmComponent, { data: dialogData });
  }

  viewKillStock(quote: IQuotation) {
    if (!quote.score) return;
    this.dialog.open(KillStockDetailsComponent, {
      data: {
        quote_name: quote.name,
        score: quote.score,
        overall_score: quote.final_score,
      },
    });
  }

  /**
   * Clears the advanced filter only — the mirror of `clearQuickFilters`. The
   * status pill and the owner selection are the quick bar's to clear, so they
   * survive this even though they share the same `advanced` object.
   */
  clearFilter() {
    this.selectedQuotations = [];
    this.page = { ...this.page, number: 1 };
    const ownership = this.unparsedFilter?.ownershipFilter;
    const cleared = refillFilter(this.module, null) as IQuotationFilter;
    // `creator` and `teams` are advanced-only and go with the rest; `owner` and
    // `my_orders` are what the bar shows, so they are carried over.
    if (ownership?.owner || ownership?.my_orders) {
      cleared.ownershipFilter = {
        ...(cleared.ownershipFilter ?? {}),
        owner: ownership.owner,
        my_orders: ownership.my_orders,
      };
    }
    this.filters.advanced = cleared;
    this.unparsedFilter = cleared;
    this.filter = parseFilterRequest(this.module, cleared);
    // Navigated after the local reset so the serialised view reflects the
    // cleared filter rather than the state being replaced.
    this.router.navigate([], {
      queryParams: this.listQueryParams(),
      queryParamsHandling: 'merge',
    });
    this.store.dispatch(
      SetQuotationFilter({
        keyword: this.key,
        advanced: this.filters.advanced,
      }),
    );
    this.store.dispatch(
      GetQuotations({
        page: { ...this.page, co_pilot: this.isCopilot ? 1 : 0 },
        filter: this.filter,
        key: this.key,
        sort_by: this.activeSortBy,
        sort_mode: this.activeSortMode,
        status: this.status,
      }),
    );
  }

  /**
   * Whether the advanced filter has anything of its OWN applied. Status and
   * ownership are excluded because the quick-filter bar shows them and clears
   * them; counting them here lit a second clear affordance whose reach the user
   * could not predict — it looked narrower than "Clear filters" but was total.
   */
  hasModuleFilters(): boolean {
    return (this.filter?.filters ?? []).some((criterion) => !QUICK_FILTER_KEYS.has(criterion.key));
  }

  // ── Quick filters (WB-8073) ─────────────────────────────────────────────────

  /** `null` when unfiltered, which is what `ui-filter-select` reads as idle. */
  get quickFilterStatus(): string | null {
    return this.selectedStatus === 'all' ? null : this.selectedStatus;
  }

  get selectedOwner(): IOrderUser | null {
    return this.unparsedFilter?.ownershipFilter?.owner ?? null;
  }

  /**
   * `my_orders` is still the filter used for "mine" — it is the key the backend
   * understands — but it now surfaces as the current user being the selected row
   * rather than as a button of its own.
   */
  get selectedOwnerId(): number | null {
    if (this.isAssignedToMe) return this.currentUserId;
    return this.selectedOwner?.id ?? null;
  }

  get isAssignedToMe(): boolean {
    // Truthy, not `=== true`: `encodeFilterObject` turns booleans into 1/0, so
    // this arrives as `1` once the filter has been through the URL.
    return !!this.unparsedFilter?.ownershipFilter?.my_orders;
  }

  get hasQuickFilters(): boolean {
    if (this.selectedStatus !== 'all') return true;
    const ownership = this.unparsedFilter?.ownershipFilter;
    return !!(ownership?.my_orders || ownership?.owner);
  }

  /** The three statuses rendered as buttons on the bar. */
  statusPrimaryChips(counts: IStatusCount | null | undefined): IFilterSelectOption[] {
    return this.statusOptions(counts).primary;
  }

  /** The rest, behind the overflow trigger. */
  statusOverflowOptions(counts: IStatusCount | null | undefined): IFilterSelectOption[] {
    return this.statusOptions(counts).overflow;
  }

  /** True while the active status is one of the overflow entries. */
  get isOverflowStatusActive(): boolean {
    return STATUS_OVERFLOW.some((option) => option.key === this.selectedStatus);
  }

  /**
   * Overflow trigger text: the chosen status once one is picked, otherwise the
   * "+N more" invitation. N is derived, not written down, so adding a status to
   * `STATUS_OVERFLOW` cannot leave the label lying.
   */
  overflowStatusLabel(counts: IStatusCount | null | undefined): string {
    if (!this.isOverflowStatusActive) {
      return this.translateService.instant('quotations_and_orders.quick_filters.more', { count: STATUS_OVERFLOW.length });
    }
    const active = this.statusOptions(counts).overflow.find((option) => option.value === this.selectedStatus);
    return active?.label ?? String(this.selectedStatus);
  }

  /**
   * Both status lists. Memoised on the counts object and the active language
   * because they are read from the template: rebuilding them every change
   * detection cycle would hand `ui-filter-select` a new array identity each time.
   */
  private statusOptions(counts: IStatusCount | null | undefined): {
    primary: IFilterSelectOption[];
    overflow: IFilterSelectOption[];
  } {
    const lang = this.translateService.currentLang;
    const cacheKey = counts ?? null;
    if (this.statusOptionsCache && this.statusOptionsCache.counts === cacheKey && this.statusOptionsCache.lang === lang) {
      return this.statusOptionsCache.options;
    }
    const build = (entry: StatusQuickFilter): IFilterSelectOption => ({
      value: entry.key,
      label: this.translateService.instant(entry.labelKey),
      count: (counts as unknown as Record<string, number | undefined>)?.[entry.key] ?? 0,
      dotColor: getQuotationStatusAccent(entry.key),
      // A chosen status wears its badge's own colours rather than the generic
      // purple active treatment, so the filter and the rows it produces agree.
      activeStyle: getQuotationStatusStyles(entry.key),
      separatorBefore: entry.separatorBefore,
    });
    const options = {
      primary: STATUS_PRIMARY.map(build),
      // The reset row leads the menu: with the three buttons outside it, this is
      // the only way back to "any status" once an overflow entry is chosen.
      overflow: [
        {
          value: null,
          label: this.translateService.instant('quotations_and_orders.quick_filters.any_status'),
          count: counts?.all ?? null,
        },
        ...STATUS_OVERFLOW.map(build),
      ],
    };
    this.statusOptionsCache = { counts: cacheKey, lang, options };
    return options;
  }

  /** Toggles one of the three status buttons — clicking the active one clears it. */
  toggleStatusChip(status: string | number | null): void {
    this.getQuotationsStatus(status === this.selectedStatus ? 'all' : ((status ?? 'all') as OrderPillKey));
  }

  isStatusChipActive(status: string | number | null): boolean {
    return status === this.selectedStatus;
  }

  /**
   * The badge colours for a selected status button, and nothing while it is
   * unselected. Same source as the table's status badge, so "Sent to ERP" is the
   * one green in both places.
   *
   * `draft` has no border colour of its own, which makes the shorthand invalid
   * and leaves the button on its default grey outline — the definition a pale
   * grey fill needs anyway, and the same way the table badge renders.
   */
  statusChipStyle(status: string | number | null): { [key: string]: string } {
    if (!this.isStatusChipActive(status)) return {};
    return getQuotationStatusStyles(String(status));
  }

  /**
   * Owner menu contents. The selected owner is prepended when the fetched page
   * does not contain them, so a filter restored from the URL renders as a name
   * and an avatar rather than as a bare id while the list loads.
   *
   * No per-owner counts: the list response carries `status_count` but has no
   * owner facet, so showing a number here would mean inventing one.
   */
  get ownerQuickFilterOptions(): IFilterSelectOption[] {
    const selected = this.selectedOwner;
    const lang = this.translateService.currentLang;
    const cache = this.ownerOptionsCache;
    if (cache && cache.users === this.ownerOptions && cache.selectedId === (selected?.id ?? null) && cache.lang === lang) {
      return cache.options;
    }
    const users = [...this.ownerOptions];
    if (selected && !users.some((user) => user.id === selected.id)) {
      users.unshift(selected);
    }
    const meBadge = this.translateService.instant('quotations_and_orders.quick_filters.me');
    const options: IFilterSelectOption[] = [
      { value: null, label: this.translateService.instant('quotations_and_orders.quick_filters.anyone') },
      ...users.map((user) => ({
        value: user.id,
        label: user.name,
        // Second line, like the inline owner picker on the table rows: two
        // colleagues with the same display name are only told apart by it.
        sublabel: user.email,
        badge: user.id === this.currentUserId ? meBadge : undefined,
        avatar: {
          initials: userInitials(user.name),
          color: getUserColor(user),
          image: user.image ?? null,
        },
      })),
    ];
    this.ownerOptionsCache = { users: this.ownerOptions, selectedId: selected?.id ?? null, lang, options };
    return options;
  }

  onStatusQuickFilterChange(value: string | number | null): void {
    this.getQuotationsStatus((value ?? 'all') as OrderPillKey);
  }

  onOwnerQuickFilterChange(value: string | number | null): void {
    if (value === null) {
      this.applyOwnershipQuickFilter(null, false);
      return;
    }
    const id = Number(value);
    // Own row -> `my_orders`, which is the key the backend has for "mine".
    // Anyone else -> `owner_id`. Never both: they narrow to one person each, so
    // ANDing them would silently return nothing whenever they disagree.
    if (id === this.currentUserId) {
      this.applyOwnershipQuickFilter(null, true);
      return;
    }
    this.applyOwnershipQuickFilter(this.resolveOwner(id), false);
  }

  /**
   * True when anything at all is narrowing the list — a quick filter, the
   * advanced filter or the search box. Drives which empty state is shown:
   * "nothing matches" is only honest when something is actually filtering.
   */
  get isFiltered(): boolean {
    if (this.hasQuickFilters) return true;
    if (this.key) return true;
    return !!this.filter?.filters?.length;
  }

  /**
   * The empty state's escape hatch: drop every narrowing at once, search box
   * included. Deliberately wider than both `clearQuickFilters` (quick filters
   * only) and `clearFilter` (which leaves the keyword in place) — a user looking
   * at an empty table wants one button that brings the orders back, not a guess
   * at which of three controls is hiding them.
   */
  clearAllFilters(): void {
    this.key = '';
    this.filters.keyword = '';
    this.searchInput.setValue('', { emitEvent: false });
    this.applyPill('all');
    // `clearFilter` deliberately preserves the owner — it is the quick bar's to
    // clear. This control is the one that means everything, so the selection is
    // dropped here before handing over.
    const ownership = this.unparsedFilter?.ownershipFilter;
    if (ownership?.owner || ownership?.my_orders) {
      this.unparsedFilter = {
        ...this.unparsedFilter,
        ownershipFilter: { ...ownership, owner: undefined, my_orders: undefined },
      } as IQuotationFilter;
    }
    this.clearFilter();
  }

  /** Clears the quick filters only — the advanced filter keeps its own clear. */
  clearQuickFilters(): void {
    const ownership = this.unparsedFilter?.ownershipFilter;
    const hadOwnership = !!(ownership?.my_orders || ownership?.owner);
    this.selectedQuotations = [];
    this.applyPill('all');
    this.page = { ...this.page, number: 1 };
    if (hadOwnership) {
      // The filterState subscription refetches, so no second request from here.
      this.applyOwnershipQuickFilter(null, false);
      return;
    }
    this.toPage(this.page);
  }

  /** Fills the owner dropdown the first time it is opened, not on page load. */
  loadOwnerOptions(): void {
    if (this.ownerOptionsLoaded) return;
    this.ownerOptionsLoaded = true;
    this.requestOwnerOptions('', true);
  }

  onOwnerSearch(term: string): void {
    this.requestOwnerOptions(term, false);
  }

  private requestOwnerOptions(term: string, initial: boolean): void {
    this.ownerOptionsLoading = true;
    this.ownerLookup$.next({ term, initial });
  }

  /**
   * The single owner-lookup subscription. `switchMap` means only the newest
   * request can write to `ownerOptions`, and the spinner is cleared here rather
   * than per-request so an abandoned request cannot turn it off while its
   * replacement is still in flight.
   */
  private watchOwnerLookups(): void {
    this.ownerLookup$
      .pipe(
        switchMap(({ term, initial }) =>
          // `listBackofficeUsers` is the cached (shareReplay) unfiltered call and
          // is what the inline owner picker already uses, so the open costs
          // nothing once either picker has been opened.
          (initial ? this.ordersService.listBackofficeUsers() : this.ordersService.searchBackofficeUsers(term)).pipe(
            map((users) => ({ users, failed: false })),
            // Inside the switchMap: a failed lookup must not kill the outer
            // subscription and leave the picker dead for the rest of the session.
            catchError(() => of({ users: [] as IOrderUser[], failed: true })),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ users, failed }) => {
        this.ownerOptions = users;
        this.ownerOptionsLoading = false;
        // Unlatch on failure, or the first open having errored would mean the
        // menu is empty for good and only a page reload brings it back.
        if (failed) this.ownerOptionsLoaded = false;
      });
  }

  private resolveOwner(id: number): IOrderUser | null {
    const fromOptions = this.ownerOptions.find((user) => user.id === id);
    if (fromOptions) return fromOptions;
    return this.selectedOwner?.id === id ? this.selectedOwner : null;
  }

  /**
   * Writes the ownership pair into `filterState.advanced` — the same slice the
   * advanced dialog owns. Dispatching is all that is needed: the `filterState`
   * subscription in `ngOnInit` re-parses the filter and refetches, which is also
   * what pushes the change into the URL.
   */
  private applyOwnershipQuickFilter(owner: IOrderUser | null, assignedToMe: boolean): void {
    const advanced = refillFilter(this.module, this.unparsedFilter) as IQuotationFilter;
    advanced.ownershipFilter = {
      ...(advanced.ownershipFilter ?? {}),
      owner: owner ?? undefined,
      my_orders: assignedToMe ? true : undefined,
    };
    this.selectedQuotations = [];
    this.page = { ...this.page, number: 1 };
    this.filters.advanced = advanced;
    this.store.dispatch(
      SetQuotationFilter({
        keyword: this.key,
        advanced,
      }),
    );
  }

  // ── Inbound order channel control ───────────────────────────────────────────
  loadInboundChannel(): void {
    if (this.inboundChannelLoaded) return;
    this.inboundChannelLoaded = true;
    this.inboundChannelService
      .getInboundChannel()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel) => (this.inboundChannel = channel),
        error: () => (this.inboundChannelLoaded = false),
      });
  }

  onInboundChannelClick(active: boolean): void {
    if (active) {
      this.toggleProcessingVisibility();
    } else {
      this.copyInboundAddress();
    }
  }

  toggleProcessingVisibility(): void {
    this.store.dispatch(ToggleProcessingVisibility());
  }

  copyInboundAddress(): void {
    const address = this.inboundChannel?.address ?? '';
    if (!address) return;
    // navigator.clipboard only exists in secure contexts (HTTPS / localhost);
    // over plain HTTP it is undefined, so fall back to execCommand.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(address).then(
        () => this.markChannelCopied(),
        () => this.fallbackCopyInboundAddress(address)
      );
    } else {
      this.fallbackCopyInboundAddress(address);
    }
  }

  private markChannelCopied(): void {
    this.channelCopied = true;
    if (this.channelCopyTimer) clearTimeout(this.channelCopyTimer);
    this.channelCopyTimer = setTimeout(() => (this.channelCopied = false), 1600);
  }

  private fallbackCopyInboundAddress(address: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = address;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      if (document.execCommand('copy')) {
        this.markChannelCopied();
      }
    } catch {
      // Copy unsupported in this context — leave the indicator unchanged.
    } finally {
      document.body.removeChild(textarea);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
    if (this.channelCopyTimer) clearTimeout(this.channelCopyTimer);
    this.expandedIndex = -1;
  }

  filters: any = {};
  search(event: any) {
    const key = event.target.value;

    if (this.filters.keyword != key) {
      this.filters.keyword = key;
      this.store.dispatch(SetQuotationFilter(this.filters));
      this.router.navigate([], {
        relativeTo: this.navigationService.getCurrentRoute(),
        queryParams: this.listQueryParams({ page: 1, keyword: key }),
        queryParamsHandling: 'merge',
      });
    }
  }
}
